import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Avatar,
  Button,
  Dropdown,
  Pagination,
  Modal,
  Spin,
  App,
} from "antd";
import { VideoCameraOutlined, MoreOutlined } from "@ant-design/icons";
import { formatTime, getTimeUntilAppointment } from "../../common/utils";
import {
  apiCancelAppointment,
  apiGetAppointments,
} from "../../api/appointments";

const Appointments = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false); // ✅ Added separate cancelling state
  const [appointments, setAppointments] = useState([]);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [meta, setMeta] = useState({
    currentPage: 1,
    perPage: 10,
    totalPages: 1,
    nextPage: null,
  });

  useEffect(() => {
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const response = await apiGetAppointments({
        page: currentPage,
        perPage: meta.perPage,
      });

      setAppointments(response.data || []);
      setMeta(response.meta || meta);
    } catch (error) {
      console.error("Error fetching appointments:", error);
      message.error("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel appointment
  const handleCancelAppointment = async () => {
    if (!selectedAppointment) return;

    try {
      setCancelling(true); // ✅ Use cancelling state
      await apiCancelAppointment(selectedAppointment.id);
      message.success("Appointment cancelled successfully");
      setCancelModalVisible(false);
      setSelectedAppointment(null);
      fetchAppointments(); // Refresh the list
    } catch (error) {
      console.error("Error cancelling appointment:", error);
      message.error("Failed to cancel appointment");
    } finally {
      setCancelling(false); // ✅ Reset cancelling state
    }
  };

  const canJoinCall = (appointment) => {
    if (!appointment?.startTimestamp) return false;

    const startTimeMs = appointment.startTimestamp * 1000; // convert to ms
    const now = Date.now();
    const fifteenMinsBefore = startTimeMs - 15 * 60 * 1000;

    return now >= fifteenMinsBefore && now <= startTimeMs + 60 * 60 * 1000;
  };

  // Handle menu click
  const handleMenuClick = (key, appointment) => {
    switch (key) {
      case "view":
        navigate(`/appointments/${appointment.id}`);
        break;
      case "reschedule":
        navigate(`/appointments/reschedule/${appointment.id}`);
        break;
      case "cancel":
        setSelectedAppointment(appointment);
        setCancelModalVisible(true);
        break;
      case "video":
        if (appointment?.id) {
          navigate(`/video-call/${appointment?.id}`);
        } else {
          message.info("Video call will start soon");
        }
        break;
      default:
        break;
    }
  };

  // Action menu for each appointment
  const actionMenu = (appointment) => ({
    items: [
      {
        key: "view",
        label: "View Details",
      },
      {
        key: "reschedule",
        label: "Reschedule",
        disabled: !appointment.reschedule,
      },
      {
        key: "cancel",
        label: "Cancel Appointment",
        disabled: !appointment.cancelAppointment,
        danger: true,
      },
    ],
    onClick: ({ key }) => handleMenuClick(key, appointment),
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Appointments</h1>
        <p className="text-gray-500 mt-1">
          View and manage your upcoming appointments
        </p>
      </div>

      {/* Upcoming Appointments Section */}
      <Card
        title={
          <span className="text-lg font-semibold text-gray-900">
            Upcoming Appointments
          </span>
        }
        className="shadow-sm rounded-lg"
      >
        {appointments.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No upcoming appointments</p>
          </div>
        ) : (
          <div className="space-y-4">
            {appointments.map((appointment) => (
              <div
                key={appointment.id}
                className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                // onClick={() => navigate(`/appointments/${appointment.id}`)}
              >
                <div className="flex items-center gap-4 flex-1">
                  <Avatar
                    size={48}
                    src={appointment?.patientImage}
                    style={{
                      backgroundColor: appointment?.patientImage
                        ? undefined
                        : "#8B5CF6",
                    }}
                  >
                    {!appointment?.patientImage &&
                      appointment.patientName?.charAt(0)?.toUpperCase()}
                  </Avatar>

                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800">
                      {appointment.patientName}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {appointment.subject || "Weight Loss Consultation"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4 flex-1">
                  <span className="text-gray-700 font-medium">
                    {formatTime(
                      appointment.startTimestamp,
                      appointment.endTimestamp
                    )}
                    {getTimeUntilAppointment(appointment.startTimestamp) && (
                      <p className="text-xs text-purple-600 mt-1">
                        {getTimeUntilAppointment(appointment.startTimestamp)}
                      </p>
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <Button
                    type="primary"
                    icon={<VideoCameraOutlined />}
                    className="bg-purple-600 hover:bg-purple-700 border-purple-600"
                    disabled={!canJoinCall(appointment) || !appointment?.joinVideoCall}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuClick("video", appointment);
                    }}
                  />
                  <Dropdown
                    menu={actionMenu(appointment)}
                    trigger={["click"]}
                    placement="bottomRight"
                  >
                    <Button
                      icon={<MoreOutlined />}
                      type="text"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Dropdown>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {appointments.length > 0 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
            <span className="text-sm text-gray-500">
              Showing {(meta.currentPage - 1) * meta.perPage + 1}-
              {Math.min(
                meta.currentPage * meta.perPage,
                meta.total
              )}{" "}
              of {meta.total} appointments
            </span>
            <Pagination
              current={meta.currentPage}
              total={meta.total}
              pageSize={meta.perPage}
              onChange={(page) => setCurrentPage(page)}
              showSizeChanger={false}
            />
          </div>
        )}
      </Card>

      {/* Cancel Appointment Modal */}
      <Modal
        open={cancelModalVisible}
        onCancel={() => {
          setCancelModalVisible(false);
          setSelectedAppointment(null);
        }}
        footer={null}
        centered
        width={500}
        closable={false}
        className="cancel-appointment-modal"
      >
        <div className="text-center py-4">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Cancel</h2>
          <p className="text-gray-600 mb-6">
            Are you sure you want to cancel the appointment?
          </p>

          <div className="flex gap-3 justify-center">
            <Button
              size="large"
              onClick={() => {
                setCancelModalVisible(false);
                setSelectedAppointment(null);
              }}
              className="px-8 min-w-[120px]"
            >
              No
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleCancelAppointment}
              loading={cancelling} // ✅ Use cancelling state instead of loading
              className="bg-purple-600 hover:bg-purple-700 border-purple-600 px-8 min-w-[120px]"
            >
              Yes
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Appointments;
